import React, { useEffect, useMemo, useState, useRef, lazy, Suspense } from "react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
} from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Youtube,
  Instagram,
  Facebook,
  Mail,
  MapPin,
  Scissors,
  Star,
  Heart,
  PlayCircle,
  Menu,
  X,
  ArrowRight,
  Search,
  ArrowLeft,
  ShoppingBag,
  Coffee,
  PenTool,
  ArrowDownAZ,
  List,
  Tag
} from "lucide-react";

// --- IMPORT PATHS CORRETTI (../ per uscire dalla cartella app)  ---
import SEO from "../components/SEO";
import { CATEGORIES, SEASONS } from "../data";
import ProjectCard from "../components/ProjectCard";
import { fetchProducts } from "../sanity/products";
import GoogleTranslateInit from "../components/GoogleTranslateInit";
import TranslateButton from "../components/TranslateButton";

const ProductDetailView = lazy(() => import("../components/ProductDetailView"));

// --- SEO HELPER ---
const SpaLink = ({ href, className, children, onClick, active }) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) onClick(e);
  };
  return (
    <a href={href} className={className} onClick={handleClick} aria-current={active ? "page" : undefined}>
      {children}
    </a>
  );
};

// --- PRELOAD HELPER ---
const preloadImage = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    if (img.complete) {
      resolve(src);
    } else {
      img.onload = () => resolve(src);
      img.onerror = () => resolve(src);
    }
  });
};

// --- COMPONENTI GRAFICI DIORAMA ---

const DioramaLayer = ({
  src,
  className,
  alt = "",
  style = {},
  priority = false,
  layerType = "static",
  delay = 0,
  depth,
  ...props
}) => {

  const getVariants = () => {
    switch (layerType) {
      case "mountain":
        return {
          initial: { y: 50, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { opacity: 0, transition: { duration: 0.3 } },
        };
      case "hill":
        return {
          initial: { y: 100, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { opacity: 0, transition: { duration: 0.3 } },
        };
      case "tree":
        return {
          initial: { y: 150, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { y: 50, opacity: 0, transition: { duration: 0.2 } },
        };
      default:
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        };
    }
  };

  const easeOutExpo = [0.16, 1, 0.3, 1];

  return (
    <motion.img
      {...props}
      alt={alt}
      draggable={false}
      src={src}
      loading="eager"
      decoding="async"
      className={`${className} max-w-none transform-gpu pointer-events-none select-none`}
      variants={getVariants()}
      transition={{
        duration: 0.8,
        ease: easeOutExpo,
        delay: delay
      }}
      style={style}
    />
  );
};

const Particles = ({ type, wind = 0 }) => {
  const prefersReducedMotion = useReducedMotion();
  const [viewport, setViewport] = useState({ w: 0, h: 0 });

  // Gestione resize finestra
  useEffect(() => {
    const update = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener("resize", update, { passive: true });
    return () => window.removeEventListener("resize", update);
  }, []);

  // Generazione particelle
  const particles = useMemo(() => {
    // Evita calcoli se la finestra non è ancora caricata
    if (viewport.w === 0) return [];

    // Mostra particelle anche su mobile (numero ridotto)
    const count = viewport.w < 768 ? 8 : 25;

    // Se l'utente ha chiesto di ridurre i movimenti o non c'è un tipo, non renderizzare
    if (prefersReducedMotion || !type) return [];

    return Array.from({ length: count }).map((_, i) => ({
      id: i,
      xStart: Math.random() * 100,
      xEnd: (Math.random() * 100) + (wind * 20),
      duration: Math.random() * 5 + 5,
      delay: -(Math.random() * 10),
      // Aumentiamo un po' la rotazione per i petali
      rotate: Math.random() * 360,
      // Variamo leggermente le dimensioni
      scale: Math.random() * 0.4 + 0.6,
    }));
  }, [type, wind, prefersReducedMotion, viewport.w]);

  if (!type || particles.length === 0 || type === 'sunrays') return null;

  // --- MODIFICA QUI ---
  const getStyle = () => {
    switch (type) {
      case "snow":
        return "bg-white w-3 h-3 rounded-full opacity-90 blur-[1px] shadow-sm";
      case "petals":
        // MODIFICATO: Forma a petalo (arrotondata diagonale), ovale, rosa più tenue
        return "bg-pink-200/90 w-3 h-4 rounded-tr-full rounded-bl-full shadow-sm";
      case "leaves":
        // Arancione scuro/marrone per autunno, forma più squadrata
        return "bg-orange-600 w-4 h-4 rounded-tr-xl rounded-bl-xl opacity-90";
      default:
        return "bg-white w-2 h-2 rounded-full";
    }
  };
  // --------------------

  return (
    <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden w-full h-full">
      <AnimatePresence>
        {particles.map((p) => (
          <motion.div
            key={`p-${type}-${p.id}`}
            className={`absolute top-[-20px] ${getStyle()} origin-center`} // origin-center aiuta la rotazione
            style={{ left: `${p.xStart}%` }}
            initial={{ y: -50, rotate: p.rotate, opacity: 0, scale: p.scale }}
            animate={{
              y: "110vh",
              x: `${wind * 80}px`, // Aumentato leggermente l'effetto vento laterale
              rotate: type === 'snow' ? p.rotate : p.rotate + 360, // Neve ruota poco, petali/foglie ruotano mentre cadono
              opacity: [0, 1, 1, 0],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              delay: p.delay,
              ease: "linear",
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// --- SINGLE SCENE WRAPPER ---
const SeasonScene = ({ currentSeason, art }) => {
  if (!art.mountains && !art.hills) return null;

  return (
    <motion.div
      className="absolute inset-0 w-full h-full pointer-events-none"
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <DioramaLayer
        src={art.mountains}
        layerType="mountain"
        delay={0}
        priority={true}
        className="absolute bottom-0 xl:bottom-24 z-10 object-cover min-h-[60vh] xl:min-h-[70vh]"
        style={{ width: "120vw", left: "-10vw" }}
      />

      <DioramaLayer
        src={art.hills}
        layerType="hill"
        delay={0.1}
        className="absolute -bottom-10 2xl:-bottom-40 z-20 object-cover min-h-[50vh]"
        style={{ width: "120vw", left: "-10vw" }}
      />

      {art.treeA && (
        <div className="absolute bottom-[-35px] left-[-60px] md:left-[-100px] lg:left-[-150px] xl:left-[-200px] 2xl:left-[-300px] z-30 pointer-events-none transform-gpu">
          <DioramaLayer
            src={art.treeA}
            layerType="tree"
            delay={0.2}
            className="w-[280px] md:w-[450px] lg:w-[500px] xl:w-[650px] 2xl:w-[900px] h-auto max-w-none"
          />
        </div>
      )}

      {art.treeB && (
        <div className="absolute bottom-[-40px] right-[-60px] md:right-[-100px] lg:right-[-150px] xl:right-[-200px] 2xl:right-[-200px] z-30 pointer-events-none transform-gpu">
          <DioramaLayer
            src={art.treeB}
            layerType="tree"
            delay={0.3}
            className="w-[260px] md:w-[400px] lg:w-[450px] xl:w-[600px] 2xl:w-[800px] h-auto max-w-none"
            style={{ transform: "scaleX(-1)" }}
          />
        </div>
      )}

      <Particles
        type={currentSeason.particles}
        wind={currentSeason.windDirection}
      />
    </motion.div>
  );
};


// --- HERO SECTION ---
const SeasonalHero = ({ scrollRef, setHeroColor, onNav }) => {
  const [index, setIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const currentSeason = SEASONS[index];

  const getCategoryFromIndex = (idx) => {
    switch (idx) {
      case 0: return "Primavera (Pasqua)";
      case 1: return "Estate";
      case 2: return "Autunno";
      case 3: return "Inverno (Natale)";
      default: return "Tutti";
    }
  };

  useEffect(() => {
    if (setHeroColor) setHeroColor(currentSeason.palette.text);
  }, [index, currentSeason, setHeroColor]);

  useEffect(() => {
    const loadAllSeasons = async () => {
      const allImages = SEASONS.flatMap(season => {
        const imgs = season.customImages || {};
        return [imgs.mountains, imgs.hills, imgs.treeLeft, imgs.treeRight].filter(Boolean);
      });
      for (const img of allImages) {
        await preloadImage(img);
      }
      setImagesLoaded(true);
    };
    loadAllSeasons();
  }, []);

  // Auto-play
  useEffect(() => {
    if (!imagesLoaded) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % SEASONS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [index, imagesLoaded]);

  const art = useMemo(() => {
    const imgs = currentSeason.customImages || {};
    return {
      mountains: imgs.mountains,
      hills: imgs.hills,
      treeA: imgs.treeLeft,
      treeB: imgs.treeRight,
    };
  }, [currentSeason]);

  const changeSeason = (direction) => {
    setIndex((prev) => {
      if (direction === "next") return (prev + 1) % SEASONS.length;
      if (direction === "prev") return prev === 0 ? SEASONS.length - 1 : prev - 1;
      return prev;
    });
  };

  const scrollToContent = () => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getBackgroundGradient = (idx) => {
    switch (idx) {
      case 0: return "linear-gradient(to bottom, #f0fdf4, #dcfce7)";
      case 1: return "linear-gradient(to bottom, #ecfccb, #bbf7d0)";
      case 2: return "linear-gradient(to bottom, #fff7ed, #ffedd5)";
      case 3: return "linear-gradient(to bottom, #f0f9ff, #dbeafe)";
      default: return "linear-gradient(to bottom, #ffffff, #f3f4f6)";
    }
  };

  const glassButtonStyle = `
    backdrop-blur-sm 
    bg-white/80 
    hover:bg-white 
    border-2 border-white 
    shadow-[0_4px_12px_rgba(0,0,0,0.15)] 
    transition-all duration-300
    cursor-pointer
  `;

  if (!imagesLoaded) {
    // Altezza caricamento sincronizzata
    return (
      <div className="h-[75vh] md:h-[95vh] w-screen flex items-center justify-center bg-craft-cream">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-craft-red border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    // MODIFICA ALTEZZA: h-[75vh] su mobile (5% in meno rispetto a prima)
    <div className="h-[80vh] md:h-[95vh] w-screen overflow-hidden relative bg-gray-100 select-none">

      {/* Sfondo Gradiente */}
      <motion.div
        className="absolute inset-0 -z-20 w-full h-full transform-gpu"
        animate={{
          background: getBackgroundGradient(index),
        }}
        transition={{ duration: 2.0, ease: "easeInOut" }}
      />

      {/* --- NESSUN ALTRO DIV DECORATIVO QUI --- */}
      {/* Se vedevi un pallino, era probabilmente un div rimasto qui. Ora è vuoto. */}

      <AnimatePresence>
        <SeasonScene
          key={currentSeason.id}
          currentSeason={currentSeason}
          art={art}
        />
      </AnimatePresence>

      {/* Contenuto Testuale Centrale */}
      <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none px-4 text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={`text-${currentSeason.id}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10, transition: { duration: 0.3 } }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-3 md:space-y-6"
          >
            <div
              className={`inline-block px-4 py-1 rounded-full text-xs font-bold tracking-[0.2em] uppercase bg-white/80 backdrop-blur-md shadow-sm ${currentSeason.palette.text}`}
            >
              Collezione {currentSeason.title}
            </div>

            <h1 className={`relative z-10 text-6xl md:text-9xl ${currentSeason.palette.text} drop-shadow-md font-hand`}>
              {currentSeason.title}
            </h1>

            <motion.button
              onClick={() => onNav("portfolio", getCategoryFromIndex(index))}
              className={`relative z-50 group flex items-center justify-center gap-2 mx-auto text-lg md:text-2xl font-bold max-w-md ${currentSeason.palette.text} cursor-pointer pointer-events-auto hover:scale-105 transition-transform mt-2`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="border-b-2 border-current pb-0.5">
                Esplora le creazioni
              </span>
              <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className={`h-1 w-20 mx-auto rounded-full ${currentSeason.palette.accent} opacity-70`}
            />
            <p
              className={`text-sm md:text-lg max-w-md mx-auto leading-relaxed ${currentSeason.palette.text} font-semibold`}
            >
              {currentSeason.description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controlli in basso */}
      <div className="absolute bottom-4 md:bottom-10 left-0 right-0 z-50 flex justify-center gap-6 items-center pointer-events-auto">
        <button
          onClick={() => changeSeason("prev")}
          className={`p-2 md:p-3 rounded-full ${glassButtonStyle} ${currentSeason.palette.text}`}
          aria-label="Stagione precedente"
        >
          <ChevronUp size={20} strokeWidth={2.5} />
        </button>

        <div className="flex gap-2">
          {SEASONS.map((s, i) => (
            <button
              key={s.id || i}
              onClick={() => setIndex(i)}
              aria-label={`Vai alla stagione ${s.title}`}
              className={`rounded-full cursor-pointer transition-all duration-300 shadow-sm border border-white/50
                ${i === index ? `w-6 h-2 md:w-8 md:h-3 ${s.palette.accent}` : `w-2 h-2 md:w-3 md:h-3 bg-white/60 hover:bg-white`}`}
            />
          ))}
        </div>

        <button
          onClick={() => changeSeason("next")}
          className={`p-2 md:p-3 rounded-full ${glassButtonStyle} ${currentSeason.palette.text}`}
          aria-label="Stagione successiva"
        >
          <ChevronDown size={20} strokeWidth={2.5} />
        </button>
      </div>

      {/* Bottone Esplora (Solo Desktop) */}
      <motion.button
        onClick={scrollToContent}
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className={`absolute bottom-4 right-6 z-50 hidden lg:flex items-center gap-2 cursor-pointer pointer-events-auto rounded-full px-6 py-2 
          ${glassButtonStyle} ${currentSeason.palette.text}`}
      >
        <span className="text-xs uppercase tracking-widest font-bold hidden md:block">Esplora</span>
        <ChevronDown className="w-5 h-5" strokeWidth={2.5} />
      </motion.button>
    </div>
  );
};

// --- YOUTUBE HOOK ---
const useLatestYouTubeVideo = () => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch("/.netlify/functions/latest-youtube", { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json?.error) throw new Error(json.error);
        if (alive) setState({ loading: false, data: json, error: null });
      } catch (e) {
        if (alive && e.name !== 'AbortError') setState({ loading: false, data: null, error: e });
      }
    })();
    return () => { alive = false; controller.abort(); };
  }, []);
  return state;
};

// --- NAVIGATION ---
const Navbar = ({ customColor, onNav, currentView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const forceDark = currentView === "portfolio" || currentView === "about" || currentView === "product";
  const effectiveColor = forceDark ? "text-craft-dark" : scrolled ? "text-craft-dark" : customColor;
  const logoColor = forceDark ? "text-craft-red" : scrolled ? "text-craft-red" : customColor;
  const bgColor = scrolled || forceDark ? "bg-white/80 backdrop-blur-md shadow-md py-3" : "bg-transparent py-5";
  const navLinks = [
    { id: "home", label: "Home", href: "/" },
    { id: "shop", label: "Shop", href: "https://www.etsy.com/it/shop/CraftOnLineShop", external: true },
    { id: "portfolio", label: "I Miei Lavori", href: "/portfolio" },
    { id: "tutorial", label: "Tutorial", href: "https://www.youtube.com/c/CraftOnLine-Italy/featured", external: true },
    { id: "chi-sono", label: "Chi Sono", href: "/chi-sono" },
  ];
  return (
    <nav className={`fixed w-full z-[100] transition-all duration-300 ${bgColor}`}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <SpaLink href="/" onClick={() => onNav("home")} className="flex items-center gap-2 group decoration-none">
          <div className="w-10 h-10 bg-craft-red rounded-full flex items-center justify-center text-white shadow-lg group-hover:rotate-12 transition-transform duration-300 border-2 border-white/50">
            <Scissors size={20} />
            <img src="/logo512.png" alt="logo" width="40" height="40" className="object-contain" />
          </div>
          <span className={`font-hand text-3xl ${logoColor} transition-colors md:drop-shadow-sm`}>CraftOnline</span>
        </SpaLink>
        <div className={`hidden md:flex items-center space-x-8 font-bold text-lg ${effectiveColor} transition-colors md:drop-shadow-sm`}>
          {navLinks.map((item) => (
            item.external ? (
              <a key={item.id} href={item.href} target="_blank" rel="noopener noreferrer" className="relative group overflow-hidden bg-transparent border-none cursor-pointer p-0 font-inherit text-inherit flex items-center gap-1">
                {item.id === "shop" && <ShoppingBag size={16} />}
                <span className="relative z-10">{item.label}</span>
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-current transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ) : (
              <SpaLink key={item.id} href={item.href} onClick={() => onNav(item.id)} className="relative group overflow-hidden bg-transparent border-none cursor-pointer p-0 font-inherit text-inherit flex items-center gap-1">
                <span className="relative z-10">{item.label}</span>
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-current transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </SpaLink>
            )
          ))}
          <TranslateButton />
        </div>
        <div className="flex items-center gap-4 md:hidden">
          <button onClick={() => setIsOpen(!isOpen)} aria-label="Menu" className={`${effectiveColor} p-2 rounded-lg hover:bg-white/20 transition-colors`}>
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="md:hidden bg-white/95 backdrop-blur-lg border-b border-gray-100 overflow-hidden shadow-xl">
            <div className="flex flex-col p-6 space-y-4 text-center font-bold text-craft-dark">
              {navLinks.map((item) => (
                item.external ? (
                  <a key={item.id} href={item.href} target="_blank" rel="noopener noreferrer" className="py-2 hover:text-craft-red hover:bg-red-50 rounded-lg transition-colors w-full block decoration-none">
                    <span className="flex items-center justify-center gap-2">{item.id === "shop" && <ShoppingBag size={18} />}{item.label}</span>
                  </a>
                ) : (
                  <SpaLink key={item.id} href={item.href} onClick={() => { onNav(item.id); setIsOpen(false); }} className="py-2 hover:text-craft-red hover:bg-red-50 rounded-lg transition-colors w-full block decoration-none">
                    {item.label}
                  </SpaLink>
                )
              ))}
              <div className="pt-2 flex justify-center border-t border-gray-100">
                <TranslateButton />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// --- AMY'S FRIENDS SLIDER ---
const AmyFriendsSlider = ({ projects = [], onOpenProject }) => {
  const amyFriends = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    return list.filter((p) => p?.category === "Amiche di Amy" || p?.tag === "amiche-di-amy");
  }, [projects]);

  const loopItems = useMemo(() => {
    if (!amyFriends.length) return [];
    const MIN_ITEMS = 6;
    let filledList = [...amyFriends];
    while (filledList.length < MIN_ITEMS) {
      filledList = [...filledList, ...amyFriends];
    }
    return [...filledList, ...filledList];
  }, [amyFriends]);

  return (
    <section className="py-24 bg-[#fff8e1] relative overflow-hidden">
      <div className="text-center mb-16 relative z-10">
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h2 className="font-hand text-5xl text-craft-dark mb-3">Le Amiche di Amy</h2>
          <p className="text-gray-500 font-medium tracking-widest text-sm uppercase">Collezione Esclusiva Fatta a Mano</p>
        </motion.div>
      </div>
      <div className="absolute top-[55%] left-0 w-full z-0"><svg width="100%" height="20" xmlns="http://www.w3.org/2000/svg"><path d="M0,10 Q50,0 100,10 T200,10 T300,10 T400,10 T500,10 T600,10 T700,10 T800,10 T900,10 T1000,10 T1100,10 T1200,10 T1300,10 T1400,10 T1500,10 T1600,10 T1700,10 T1800,10 T1900,10 T2000,10" stroke="#e6d5c3" strokeWidth="4" fill="none" strokeDasharray="10,5" className="w-full" style={{ transform: "scaleX(2)" }} /></svg></div>
      <div className="relative w-full flex overflow-hidden py-20">
        {loopItems.length ? (
          <motion.div
            className="flex items-center px-4 will-change-transform"
            animate={{ x: "-50%" }}
            transition={{ ease: "linear", duration: loopItems.length * 3.5, repeat: Infinity }}
          >
            {loopItems.map((item, index) => {
              const rotation = index % 2 === 0 ? 3 : -3;
              const buttonColor = index % 3 === 0 ? "bg-red-400" : index % 3 === 1 ? "bg-blue-400" : "bg-yellow-400";
              const uniqueKey = item.id ? `${item.id}-${index}` : `amy-friend-${index}`;
              return (
                <div key={uniqueKey} className="relative flex-shrink-0 mx-6 group cursor-pointer w-64 pt-8" onClick={() => onOpenProject && onOpenProject(item)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpenProject && onOpenProject(item); }}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-10 bg-gray-300 z-0" />
                  <div className={`absolute top-6 left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full ${buttonColor} border-2 border-white shadow-sm z-40 flex items-center justify-center`}>
                    <div className="flex gap-0.5"><div className="w-1 h-1 bg-white/70 rounded-full" /><div className="w-1 h-1 bg-white/70 rounded-full" /></div>
                  </div>
                  <div className="bg-white p-3 shadow-lg rounded-2xl relative z-10 transform transition-all duration-300 group-hover:scale-105 group-hover:z-30 group-hover:shadow-2xl" style={{ transform: `rotate(${rotation}deg)` }}>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 h-full">
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-gray-50 mb-3">
                        <img src={item.img} alt={item.title || item.name} loading="lazy" decoding="async" className="w-full h-full object-cover object-center opacity-90 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-center pb-1"><span translate="no" className="notranslate font-hand text-2xl text-craft-dark">{item.title || item.name}</span></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        ) : (
          <div className="w-full text-center text-gray-500 font-medium">Nessun prodotto trovato nella categoria “Amiche di Amy”.</div>
        )}
      </div>
    </section>
  );
};

// --- HOME SHOWCASE ---
const HomeShowcaseSection = ({ id, onSeeAll, onOpenProject, projects }) => {
  const previewProjects = (projects || []).slice(0, 3);
  return (
    <section id={id} className="py-24 relative bg-craft-cream overflow-hidden">
      <div className="absolute inset-0 bg-pattern-dots pointer-events-none" />
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <span className="text-craft-red font-bold tracking-widest text-sm uppercase mb-2 block">Anteprima</span>
            <h2 className="font-hand text-5xl md:text-6xl text-craft-dark mb-4 relative inline-block">Le Mie Creazioni<span className="absolute -bottom-2 left-0 w-full h-3 bg-red-200/50 -z-10 rounded-full transform -rotate-1" /></h2>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg mt-6 leading-relaxed">Ogni pezzo è unico, fatto a mano con amore. Questa è solo una piccola selezione dei miei lavori.</p>
          </motion.div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
          {previewProjects.map((project, i) => (
            <ProjectCard key={project.id || `proj-${i}`} project={project} showPatternBadge={false} onOpen={onOpenProject} />
          ))}
        </div>
        <div className="text-center">
          <SpaLink href="/portfolio" onClick={() => onSeeAll()} className="inline-flex items-center gap-2 px-10 py-4 bg-craft-red text-white font-bold rounded-full shadow-lg hover:bg-red-800 transition-all hover:scale-105 hover:shadow-xl cursor-pointer decoration-none">
            Vedi Portfolio Completo <ArrowRight size={20} />
          </SpaLink>
        </div>
      </div>
    </section>
  );
};

// --- PORTFOLIO VIEW ---
const PortfolioView = ({ onBack, onOpenProject, projects, initialFilter }) => {
  const [filter, setFilter] = useState(initialFilter || "Tutti");
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState("default");

  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filteredProjects = (projects || []).filter((project) => {
    const matchesCategory = filter === "Tutti" || project.category === filter;
    const matchesSearch = project.title.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (sortOption === "alpha") return a.title.localeCompare(b.title);
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (db === da) return a.title.localeCompare(b.title);
    return db - da;
  });

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filter, search, sortOption, PAGE_SIZE, projects]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || visibleCount >= sortedProjects.length) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisibleCount((c) => Math.min(c + PAGE_SIZE, sortedProjects.length));
    }, { root: null, threshold: 0, rootMargin: "800px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visibleCount, sortedProjects.length, PAGE_SIZE]);

  const visibleProjects = useMemo(() => sortedProjects.slice(0, visibleCount), [sortedProjects, visibleCount]);

  const renderCategoryButtons = () => (
    CATEGORIES.map((cat) => (
      <button
        key={cat}
        onClick={() => setFilter(cat)}
        className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 border-2 cursor-pointer flex-shrink-0 
        ${filter === cat
            ? "bg-craft-red text-white border-craft-red shadow-md transform scale-105"
            : "bg-white text-gray-500 border-gray-100 hover:border-red-200 hover:text-craft-red hover:bg-red-50"}`}
      >
        {cat}
      </button>
    ))
  );

  return (
    // MODIFICA 3: Rimosso pb-10/pb-20 dal main container e aggiunto flex flex-col per gestire il footer flush
    <div className="min-h-screen bg-craft-cream pt-24 relative flex flex-col">
      <div className="absolute inset-0 bg-pattern-dots pointer-events-none" />

      {/* Container interno che si espande */}
      <div className="container mx-auto px-4 md:px-6 relative z-10 flex flex-col flex-grow">

        {/* Header */}
        <div className="mb-4 md:mb-12">
          <SpaLink href="/" onClick={onBack} className="flex items-center gap-2 text-craft-red font-bold hover:underline mb-4 md:mb-6 cursor-pointer decoration-none">
            <ArrowLeft size={20} /> Torna alla Home
          </SpaLink>
          <h1 className="font-hand text-4xl md:text-7xl text-craft-dark mb-2 md:mb-4">Tutte le mie creazioni</h1>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl hidden md:block">
            Esplora l'intera collezione. Usa i filtri per trovare esattamente ciò che cerchi, inclusi i progetti con cartamodello gratuito.
          </p>
        </div>

        {/* --- MOBILE: RICERCA (NON STICKY) --- */}
        {/* MODIFICA 1: Rimossa classe 'sticky top-...' e aggiunta 'mb-6' */}
        <div className="block md:hidden relative z-30 mb-6">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Cerca progetto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-gray-200 focus:border-craft-red focus:ring-2 focus:ring-red-100 outline-none transition-all bg-white text-craft-dark font-medium placeholder-gray-400 shadow-inner"
            />
          </div>
        </div>

        {/* --- DESKTOP: FILTRI COMPLETI (Restato Invariato) --- */}
        <div className="hidden md:block sticky top-24 z-30 mb-12 bg-white/80 backdrop-blur-md p-6 rounded-2xl stitched-border border-craft-red/20 shadow-lg">
          <div className="flex flex-row items-center justify-between gap-6">
            <div className="flex overflow-x-auto w-auto p-2 gap-2 no-scrollbar scroll-smooth items-center">
              {renderCategoryButtons()}
            </div>
            <div className="flex flex-row gap-4 w-auto flex-shrink-0">
              <div className="relative min-w-[160px] max-w-[200px] w-auto">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                  {sortOption === "alpha" ? <ArrowDownAZ size={20} /> : <List size={20} />}
                </div>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 rounded-full border-2 border-gray-200 focus:border-craft-red focus:ring-2 focus:ring-red-100 outline-none transition-all bg-white text-craft-dark font-medium appearance-none cursor-pointer shadow-inner hover:border-red-200"
                >
                  <option value="alpha">Alfabetico</option>
                  <option value="default">Data (Recenti)</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
              </div>
              <div className="relative w-80">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Cerca progetto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-gray-200 focus:border-craft-red focus:ring-2 focus:ring-red-100 outline-none transition-all bg-white text-craft-dark font-medium placeholder-gray-400 shadow-inner"
                />
              </div>
            </div>
          </div>
        </div>

        {/* GRIGLIA PRODOTTI */}
        {/* Usiamo flex-grow per riempire lo spazio se ci sono pochi prodotti. 
            Su mobile, mettiamo pb-24 per assicurarci che l'ultimo prodotto non finisca SOTTO la barra filtri. */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 min-h-[300px] flex-grow pb-24 md:pb-10">
          <AnimatePresence mode="popLayout">
            {visibleProjects.map((project, i) => (
              <ProjectCard key={project.id || `p-${i}`} project={project} showPatternBadge={true} onOpen={onOpenProject} />
            ))}
          </AnimatePresence>
          {sortedProjects.length === 0 && (
            <div className="col-span-full text-center py-32">
              <Scissors size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-2xl font-hand">Nessun progetto trovato per questi filtri.</p>
              <button onClick={() => { setFilter("Tutti"); setSearch(""); }} className="mt-4 text-craft-red font-bold hover:underline cursor-pointer">Resetta filtri</button>
            </div>
          )}
        </motion.div>

        {sortedProjects.length > 0 && visibleCount < sortedProjects.length && (
          <div className="flex justify-center py-10 pb-32 md:pb-10">
            <div ref={loadMoreRef} className="flex items-center gap-3 text-gray-500 font-medium"><div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" aria-hidden="true" />Carico altri prodotti…</div>
          </div>
        )}

        {sortedProjects.length > 0 && visibleCount >= sortedProjects.length && (
          <div className="text-center py-10 pb-32 md:pb-10 text-gray-400 font-medium">Hai visto tutti i prodotti.</div>
        )}

      </div>

      {/* --- MOBILE: CATEGORIE (Sticky Bottom + Flush Footer) --- */}
      {/* MODIFICA 3 (Flush): mt-auto spinge questo blocco in fondo al flex parent. Essendo sticky, galleggia finché non tocca il fondo. */}
      {/* MODIFICA 2 (Ombre): Aggiunto py-4 nel div interno */}
      <div className="block md:hidden sticky bottom-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-5px_20px_rgba(0,0,0,0.1)] mt-auto">
        <div className="flex overflow-x-auto w-full gap-2 no-scrollbar scroll-smooth items-center px-4 py-4">
          {renderCategoryButtons()}
        </div>
      </div>
    </div>
  );
};

// --- ABOUT VIEW (Stesso di prima) ---
const AboutView = ({ onBack }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const stats = [
    { label: "Anni di Cucito", value: "30+", icon: <Scissors size={24} /> },
    { label: "Caffè Bevuti", value: "5k+", icon: <Coffee size={24} /> },
    { label: "Progetti Creati", value: "180+", icon: <PenTool size={24} /> },
    { label: "Amore", value: "∞", icon: <Heart size={24} /> },
  ];
  return (
    <div className="min-h-screen bg-[#fff8e1] pt-24 pb-20 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-100/50 rounded-full blur-3xl opacity-60 -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-100/50 rounded-full blur-3xl opacity-60 translate-y-1/4 -translate-x-1/4 pointer-events-none" />
      <div className="container mx-auto px-6 relative z-10">
        <SpaLink href="/" onClick={onBack} className="flex items-center gap-2 text-craft-red font-bold hover:underline mb-12 cursor-pointer transition-transform hover:-translate-x-1 decoration-none"><ArrowLeft size={20} /> Torna alla Home</SpaLink>
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -50, rotate: -2 }} animate={{ opacity: 1, x: 0, rotate: 0 }} transition={{ duration: 0.8 }} className="lg:w-5/12 w-full">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-4 border-craft-red/20 rounded-[3rem] transform rotate-3" />
              <div className="absolute -bottom-4 -right-4 w-full h-full border-4 border-yellow-400/20 rounded-[3rem] transform -rotate-2" />
              <div className="relative overflow-hidden rounded-[3rem] shadow-2xl aspect-[4/5] bg-white border-8 border-white">
                <img src="monica.jpeg" alt="Ritratto Creativa" loading="lazy" decoding="async" className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-[#4e342e]/80 to-transparent p-8 text-white"><p className="font-hand text-3xl">Ciao, sono Monica!</p><p className="text-sm opacity-90">Creatrice di sogni fatti a mano</p></div>
              </div>
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }} className="absolute -top-6 -right-6 bg-white p-3 rounded-full shadow-lg text-craft-red"><Heart size={32} fill="currentColor" /></motion.div>
            </div>
          </motion.div>
          <motion.div className="lg:w-7/12 w-full space-y-8" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div><h1 className="font-hand text-6xl text-craft-dark mb-4">Dietro l'Ago e il Filo</h1><div className="h-1.5 w-32 bg-craft-red rounded-full opacity-60" /></div>
            <div className="text-lg text-gray-700 leading-relaxed space-y-6">
              <p>Quando ero piccola adoravo lavorare con tessuti e colori. Mi piaceva fare i centrotavola di Natale o di Pasqua, decoravo il mio diario di scuola, vestivo le mie bambole. Usavo tutto quello che avevo in casa, bottoni, pezzetti di passamaneria, resti di tessuto, pennelli e colori, carte colorate, stampe e ritagli di giornale.</p>
              <p>Poi un giorno sono cresciuta e ho conosciuto creative fantastiche, ognuna di loro mi ha dato un pezzetto di sé che ho messo nei miei lavori che crescevano con me.</p>
              <p>Credo che ogni oggetto fatto a mano abbia un'anima. Non si tratta solo di cucire due pezzi di stoffa insieme, ma di intrecciare ricordi, pazienza e un pizzico di magia. Che sia una bambola per la tua piccola o una borsa per le tue avventure, ogni mia creazione porta con sé un pezzo del mio cuore.</p>
              <p className="font-semibold text-craft-dark border-l-4 border-craft-red pl-4 italic">"La creatività è contagiosa. Trasmettila." – Albert Einstein</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
              {stats.map((stat, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }} className="bg-white p-4 rounded-2xl shadow-md border border-gray-100 text-center hover:-translate-y-1 transition-transform">
                  <div className="text-craft-red mb-2 flex justify-center">{stat.icon}</div><div className="font-bold text-2xl text-craft-dark mb-1 font-hand">{stat.value}</div><div className="text-xs text-gray-500 uppercase tracking-wide font-bold">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            <div className="pt-6">
              <h3 className="font-hand text-3xl text-craft-dark mb-4">La mia Filosofia</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">1</div><span className="text-gray-700">Materiali selezionati e di alta qualità.</span></li>
                <li className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">2</div><span className="text-gray-700">Creazioni uniche, mai prodotti in serie.</span></li>
                <li className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-bold">3</div><span className="text-gray-700">Condivisione della conoscenza attraverso tutorial.</span></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// --- TUTORIAL SECTION (Stesso di prima) ---
const TutorialSection = ({ id }) => {
  const { loading, data, error } = useLatestYouTubeVideo();
  const thumb = data?.thumbnailUrl || "https://images.unsplash.com/photo-1528696892704-5e1122852276?auto=format&fit=crop&q=80&w=800&h=450";
  const title = loading ? "Caricamento…" : data?.title || "Ultimo Video";
  const duration = data?.duration || "";
  const href = data?.link || "https://www.youtube.com/c/CraftOnLine-Italy/featured";
  const embedUrl = data?.videoId ? `https://www.youtube.com/embed/${data.videoId}` : null;
  return (
    <section id={id} className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-50 rounded-full blur-3xl opacity-50 translate-y-1/3 -translate-x-1/4" />
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-16">
          <motion.div className="md:w-1/2" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <h2 className="font-hand text-5xl text-craft-dark mb-6">Impara l'Arte del Cucito</h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">Non c'è niente di meglio che condividere la conoscenza. Che tu sia una principiante assoluta o un'esperta in cerca di nuove sfide, troverai ispirazione nei miei tutorial.</p>
            <div className="space-y-6">
              <div className="flex items-center gap-4 group p-4 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center text-craft-red shrink-0 shadow-sm group-hover:scale-110 transition-transform"><PlayCircle size={28} /></div>
                <div><h4 className="font-bold text-xl text-craft-dark">Video Passo-Passo</h4><p className="text-gray-500 text-sm">Guide visive dettagliate per ogni punto.</p></div>
              </div>
              <div className="flex items-center gap-4 group p-4 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0 shadow-sm group-hover:scale-110 transition-transform"><Scissors size={28} /></div>
                <div><h4 className="font-bold text-xl text-craft-dark">Cartamodelli PDF</h4><p className="text-gray-500 text-sm">Scarica e stampa i progetti gratuiti da casa tua.</p></div>
              </div>
              <div className="flex items-center gap-4 group p-4 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 shrink-0 shadow-sm group-hover:scale-110 transition-transform"><Star size={28} /></div>
                <div><h4 className="font-bold text-xl text-craft-dark">Consigli & Trucchi</h4><p className="text-gray-500 text-sm">I segreti del mestiere per creazioni adorabili.</p></div>
              </div>
            </div>
            <div className="mt-10">
              <a href="https://www.youtube.com/c/CraftOnLine-Italy/featured" target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 text-red-600 font-bold hover:text-red-800 transition-colors border-b-2 border-red-200 hover:border-red-600 pb-1"><Youtube /> Visita il mio Canale YouTube</a>
            </div>
          </motion.div>
          <motion.div className="md:w-1/2 relative perspective-1000" initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <div className="absolute inset-0 bg-craft-red rounded-full blur-3xl opacity-20 transform scale-90 translate-y-4" />
            <div className="bg-white p-4 rounded-[2rem] shadow-2xl relative rotate-3 hover:rotate-0 transition-transform duration-500 border border-gray-100">
              <div className="relative overflow-hidden rounded-2xl aspect-video group block bg-black">
                {embedUrl ? (
                  <iframe title={title} src={embedUrl} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                ) : (
                  <a href={href} target="_blank" rel="noreferrer" className="block w-full h-full">
                    <img src={thumb} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-red-600 shadow-xl group-hover:scale-110 transition-transform"><PlayCircle size={40} fill="currentColor" className="ml-1" /></div>
                    </div>
                  </a>
                )}
              </div>
              <div className="mt-4 flex justify-between items-end px-2">
                <div><span className="text-xs font-bold text-gray-400 uppercase">Ultimo Video</span><h3 className="font-hand text-2xl text-craft-dark">{title}</h3>{error ? <p className="text-sm text-red-600 mt-1">Impossibile caricare l’ultimo video. ({String(error?.message || error)})</p> : null}</div>
                {duration ? <span className="text-sm font-bold text-gray-400">{duration}</span> : null}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- FOOTER ---
const Footer = ({ id, onNav }) => (
  <footer id={id} className="bg-[#4e342e] text-[#d7ccc8] pt-24 pb-10 relative overflow-hidden">
    <div className="container mx-auto px-6 relative z-10">

      {/* Layout Principale: Flexbox per separare Sinistra (Brand) e Destra (Link) */}
      <div className="flex flex-col md:flex-row justify-between gap-12 mb-16">

        {/* LATO SINISTRO: Logo, Descrizione, Social */}
        <div className="w-full md:w-5/12 lg:w-4/12 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start mb-6">
            <div className="w-10 h-10 bg-craft-red rounded-full flex items-center justify-center text-[#fff8e1] shadow-md border-2 border-[#fff8e1]/20">
              <Scissors size={20} />
              <img src="/logo512.png" alt="logo" width="40" height="40" className="object-contain" />
            </div>
            <h3 className="font-hand text-3xl text-[#fff8e1]">CraftOnline</h3>
          </div>
          <p className="text-[#d7ccc8] text-sm leading-relaxed mb-6">
            Condividiamo la passione per il cucito creativo. Unisciti alla nostra community per ispirazione quotidiana e progetti unici.
          </p>
          <div className="flex justify-center md:justify-start gap-4">
            <a href="https://it-it.facebook.com/monicamcroll/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-[#fff8e1] hover:bg-blue-600 hover:text-white transition-all hover:-translate-y-1"><Facebook size={18} /></a>
            <a href="https://www.instagram.com/monica_craftonline/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-[#fff8e1] hover:bg-pink-600 hover:text-white transition-all hover:-translate-y-1"><Instagram size={18} /></a>
            <a href="https://www.youtube.com/c/CraftOnLine-Italy/featured" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-[#fff8e1] hover:bg-red-600 hover:text-white transition-all hover:-translate-y-1"><Youtube size={18} /></a>
          </div>
        </div>

        {/* LATO DESTRO: Navigazione e Contatti raggruppati */}
        <div className="w-full md:w-6/12 flex flex-col sm:flex-row justify-center md:justify-end gap-10 sm:gap-16 md:gap-24 text-center md:text-left">

          {/* Colonna Navigazione */}
          <div>
            <h4 className="text-lg mb-6 text-[#fff8e1] font-hand">Navigazione</h4>
            <ul className="space-y-3 text-[#d7ccc8] text-sm">
              <li><SpaLink href="/" onClick={() => onNav("home")} className="hover:text-craft-red transition-colors decoration-none text-inherit">Home Page</SpaLink></li>
              <li><a href="https://www.etsy.com/it/shop/CraftOnLineShop" target="_blank" rel="noopener noreferrer" className="hover:text-craft-red transition-colors decoration-none text-inherit">Etsy Shop</a></li>
              <li><SpaLink href="/portfolio" onClick={() => onNav("portfolio")} className="hover:text-craft-red transition-colors decoration-none text-inherit">Portfolio Lavori</SpaLink></li>
              <li><a href="https://www.youtube.com/c/CraftOnLine-Italy/featured" target="_blank" rel="noopener noreferrer" className="hover:text-craft-red transition-colors decoration-none text-inherit">Video Tutorial</a></li>
              <li><SpaLink href="/chi-sono" onClick={() => onNav("chi-sono")} className="hover:text-craft-red transition-colors decoration-none text-inherit">Chi Sono</SpaLink></li>
            </ul>
          </div>

          {/* Colonna Contatti */}
          <div>
            <h4 className="text-lg mb-6 text-[#fff8e1] font-hand">Contatti</h4>
            <ul className="space-y-4 text-[#d7ccc8] text-sm mb-6">
              <li className="flex items-center justify-center md:justify-start gap-3">
                <Mail size={16} className="text-craft-red" />
                <a href="mailto:info.craftonline@gmail.com" className="decoration-none text-inherit hover:underline">info.craftonline@gmail.com</a>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3">
                <MapPin size={16} className="text-craft-red" />
                <span>Treviso, IT</span>
              </li>
            </ul>
            <div className="flex justify-center md:justify-start mt-4">
              <TranslateButton />
            </div>
          </div>

        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-[#aca19d] gap-4">
        <p>&copy; {new Date().getFullYear()} CraftOnline. Tutti i diritti riservati.</p>
        <p className="flex items-center gap-1">Realizzato con <Heart size={10} className="text-red-500 fill-current" /> da Mattia Mattarollo.</p>
      </div>
    </div>
  </footer>
);

// --- APP MAIN ---
const App = () => {
  const contentRef = useRef(null);
  const [heroColor, setHeroColor] = useState("text-white");
  const [view, setView] = useState("home");
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // STATO PER IL FILTRO DEL PORTFOLIO
  const [portfolioCategory, setPortfolioCategory] = useState("Tutti");

  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const items = await fetchProducts(); if (!alive) return; setProjects(items); } catch (e) { console.error("Sanity fetchProducts failed", e); } finally { if (alive) setProjectsLoaded(true); }
    })();
    return () => { alive = false; };
  }, []);

  const navigate = (path) => { try { if (window.location.pathname !== path) window.history.pushState({}, "", path); } catch { } };

  const applyRoute = (pathname) => {
    const p = (pathname || "/").replace(/\/$/, "") || "/";
    if (p === "/portfolio") { setView("portfolio"); setSelectedProjectId(null); return; }
    if (p === "/chi-sono") { setView("about"); setSelectedProjectId(null); return; }
    const m = p.match(/^\/progetto\/([^/]+)$/);
    if (m) { setView("product"); setSelectedProjectId(m[1]); return; }
    setView("home"); setSelectedProjectId(null);
  };
  useEffect(() => { applyRoute(window.location.pathname); const onPop = () => applyRoute(window.location.pathname); window.addEventListener("popstate", onPop); return () => window.removeEventListener("popstate", onPop); }, []);

  // AGGIORNATO: handleNav ora accetta una 'category' opzionale
  const handleNav = (target, category = null) => {
    if (target === "home") {
      setView("home");
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 10);
    }
    else if (target === "portfolio") {
      // Se c'è una categoria specifica (es. "Primavera"), la impostiamo
      if (category) setPortfolioCategory(category);
      else setPortfolioCategory("Tutti");

      setView("portfolio");
      navigate("/portfolio");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    else if (target === "chi-sono") {
      setView("about");
      navigate("/chi-sono");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    else {
      if (view !== "home") {
        setView("home");
        navigate("/");
        setTimeout(() => { const el = document.getElementById(target); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 100);
      } else {
        navigate("/");
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleOpenProject = (project) => { if (!project?.id) return; setSelectedProjectId(project.id); setView("product"); navigate(`/progetto/${project.id}`); window.scrollTo(0, 0); };
  const getSelectedProject = () => (projects || []).find((p) => p.id === selectedProjectId);

  return (
    <>
      <GoogleTranslateInit />
      <SEO view={view} project={getSelectedProject()} />
      <Navbar customColor={heroColor} onNav={handleNav} currentView={view} />
      {view === "home" && (
        <>
          {/* Passiamo handleNav a SeasonalHero */}
          <SeasonalHero scrollRef={contentRef} setHeroColor={setHeroColor} onNav={handleNav} />
          <main ref={contentRef} className="relative z-10 bg-craft-cream">
            <HomeShowcaseSection id="lavori" onSeeAll={() => handleNav("portfolio")} onOpenProject={handleOpenProject} projects={projects} />
            <TutorialSection id="tutorial-section" />
            <AmyFriendsSlider projects={projectsLoaded ? projects : []} onOpenProject={handleOpenProject} />
          </main>
        </>
      )}
      {view === "portfolio" && (
        // Passiamo portfolioCategory a PortfolioView
        <PortfolioView
          onBack={() => handleNav("home")}
          onOpenProject={handleOpenProject}
          projects={projects}
          initialFilter={portfolioCategory}
        />
      )}
      {view === "about" && <AboutView onBack={() => handleNav("home")} />}
      {view === "product" && selectedProjectId != null && <Suspense fallback={<div className="min-h-screen bg-craft-cream pt-32 text-center text-gray-500 font-medium">Caricamento…</div>}><ProductDetailView project={getSelectedProject()} onBack={() => handleNav("portfolio")} /></Suspense>}
      <Footer id="footer" onNav={handleNav} />
    </>
  );
};

export default App;